// Fixture builders for the "Longer-term optimizer" pipeline on the Optimizer
// page (comps + channel listing status; target/floor rent itself lives on
// StrategyTargets). Mirrors the day-detail-mocks.ts convention: deterministic,
// address-keyed module-level builders/consts so the same property always
// shows the same demo numbers. All money is stored in CENTS to match
// RentalStrategyService; formatting to dollars happens in the component.

export type MidLongTerm = 'mid' | 'long';

// ---------- Comps ----------

export type CompSource = 'Furnished Finder' | 'Zillow' | 'Apartments.com' | 'Zumper';

export interface CompListing {
  id: string;
  name: string;
  source: CompSource;
  beds: number;
  baths: number;
  sqft: number;
  monthlyCents: number;
  distanceMi: number;
  daysOnMarket: number;
}

const MID_COMPS_BY_ADDRESS: Record<number, CompListing[]> = {
  990001: [
    { id: 'mc1-1', name: 'Furnished 2BR · Meeting Street',      source: 'Furnished Finder', beds: 2, baths: 2,   sqft: 980,  monthlyCents: 445000, distanceMi: 0.6, daysOnMarket: 14 },
    { id: 'mc1-2', name: 'Executive Suite · Harbor Row',        source: 'Zillow',            beds: 2, baths: 2,   sqft: 1040, monthlyCents: 468000, distanceMi: 1.1, daysOnMarket: 21 },
    { id: 'mc1-3', name: 'Furnished Craftsman · Eastside',      source: 'Furnished Finder', beds: 2, baths: 1.5, sqft: 900,  monthlyCents: 415000, distanceMi: 1.8, daysOnMarket: 9  },
    { id: 'mc1-4', name: 'Corporate 2BR · The Cooper',          source: 'Apartments.com',    beds: 2, baths: 2,   sqft: 1010, monthlyCents: 452000, distanceMi: 2.3, daysOnMarket: 27 },
    { id: 'mc1-5', name: 'Modern Furnished Flat · Bayview',     source: 'Zumper',             beds: 2, baths: 2,   sqft: 960,  monthlyCents: 439000, distanceMi: 0.9, daysOnMarket: 17 },
  ],
  990002: [
    { id: 'mc2-1', name: 'Furnished Bungalow · Lake Eola',      source: 'Furnished Finder', beds: 3, baths: 2,   sqft: 1450, monthlyCents: 520000, distanceMi: 1.2, daysOnMarket: 19 },
    { id: 'mc2-2', name: 'Furnished Condo · Thornton Park',     source: 'Zumper',             beds: 3, baths: 2,   sqft: 1380, monthlyCents: 498000, distanceMi: 0.7, daysOnMarket: 11 },
    { id: 'mc2-3', name: 'Lakeview Executive Rental',           source: 'Zillow',            beds: 3, baths: 2.5, sqft: 1520, monthlyCents: 545000, distanceMi: 2.0, daysOnMarket: 24 },
    { id: 'mc2-4', name: 'Furnished Cottage · Audubon Park',    source: 'Furnished Finder', beds: 3, baths: 2,   sqft: 1410, monthlyCents: 510000, distanceMi: 0.4, daysOnMarket: 8  },
  ],
  990003: [
    { id: 'mc3-1', name: 'Furnished Cottage · Hyde Park',       source: 'Furnished Finder', beds: 1, baths: 1,   sqft: 640,  monthlyCents: 285000, distanceMi: 0.8, daysOnMarket: 16 },
    { id: 'mc3-2', name: 'Furnished Studio · North End',        source: 'Zumper',             beds: 1, baths: 1,   sqft: 590,  monthlyCents: 265000, distanceMi: 1.4, daysOnMarket: 22 },
    { id: 'mc3-3', name: 'Corporate 1BR · Linen District',      source: 'Apartments.com',    beds: 1, baths: 1,   sqft: 615,  monthlyCents: 272000, distanceMi: 1.9, daysOnMarket: 13 },
    { id: 'mc3-4', name: 'Furnished Suite · Old Boise',         source: 'Furnished Finder', beds: 1, baths: 1,   sqft: 660,  monthlyCents: 298000, distanceMi: 0.5, daysOnMarket: 7  },
  ],
};

const LONG_COMPS_BY_ADDRESS: Record<number, CompListing[]> = {
  990001: [
    { id: 'lc1-1', name: 'Unfurnished 2BR · Meeting Street',    source: 'Zillow',            beds: 2, baths: 2,   sqft: 980,  monthlyCents: 315000, distanceMi: 0.6, daysOnMarket: 33 },
    { id: 'lc1-2', name: '2BR/2BA · Harbor Row',                source: 'Apartments.com',    beds: 2, baths: 2,   sqft: 1040, monthlyCents: 329000, distanceMi: 1.1, daysOnMarket: 29 },
    { id: 'lc1-3', name: 'Craftsman 2BR · Eastside',            source: 'Zumper',             beds: 2, baths: 1.5, sqft: 900,  monthlyCents: 298000, distanceMi: 1.8, daysOnMarket: 41 },
    { id: 'lc1-4', name: '2BR Flat · The Cooper',               source: 'Zillow',            beds: 2, baths: 2,   sqft: 1010, monthlyCents: 322000, distanceMi: 2.3, daysOnMarket: 25 },
    { id: 'lc1-5', name: 'Bayview 2BR Rental',                  source: 'Apartments.com',    beds: 2, baths: 2,   sqft: 960,  monthlyCents: 309000, distanceMi: 0.9, daysOnMarket: 37 },
  ],
  990002: [
    { id: 'lc2-1', name: 'Lake Eola 3BR Bungalow',              source: 'Zillow',            beds: 3, baths: 2,   sqft: 1450, monthlyCents: 385000, distanceMi: 1.2, daysOnMarket: 30 },
    { id: 'lc2-2', name: 'Thornton Park 3BR Condo',             source: 'Apartments.com',    beds: 3, baths: 2,   sqft: 1380, monthlyCents: 365000, distanceMi: 0.7, daysOnMarket: 22 },
    { id: 'lc2-3', name: 'Lakeview 3BR/2.5BA',                  source: 'Zumper',             beds: 3, baths: 2.5, sqft: 1520, monthlyCents: 402000, distanceMi: 2.0, daysOnMarket: 44 },
    { id: 'lc2-4', name: 'Audubon Park 3BR Cottage',            source: 'Zillow',            beds: 3, baths: 2,   sqft: 1410, monthlyCents: 378000, distanceMi: 0.4, daysOnMarket: 19 },
  ],
  990003: [
    { id: 'lc3-1', name: 'Hyde Park 1BR Cottage',               source: 'Apartments.com',    beds: 1, baths: 1,   sqft: 640,  monthlyCents: 198000, distanceMi: 0.8, daysOnMarket: 35 },
    { id: 'lc3-2', name: 'North End Studio',                    source: 'Zumper',             beds: 1, baths: 1,   sqft: 590,  monthlyCents: 182000, distanceMi: 1.4, daysOnMarket: 40 },
    { id: 'lc3-3', name: 'Linen District 1BR/1BA',              source: 'Zillow',            beds: 1, baths: 1,   sqft: 615,  monthlyCents: 189000, distanceMi: 1.9, daysOnMarket: 27 },
    { id: 'lc3-4', name: 'Old Boise 1BR Suite',                 source: 'Apartments.com',    beds: 1, baths: 1,   sqft: 660,  monthlyCents: 205000, distanceMi: 0.5, daysOnMarket: 15 },
  ],
};

export function getComps(addressId: number, term: MidLongTerm): CompListing[] {
  const table = term === 'mid' ? MID_COMPS_BY_ADDRESS : LONG_COMPS_BY_ADDRESS;
  return table[addressId] ?? table[990001];
}

export interface CompSummary {
  medianCents: number;
  lowCents: number;
  highCents: number;
}

// Median + a suggested range (±6%) — computed once per call, never in the template.
export function summarizeComps(comps: CompListing[]): CompSummary {
  const sorted = [...comps].map(c => c.monthlyCents).sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const medianCents = sorted.length % 2 === 0
    ? Math.round((sorted[mid - 1] + sorted[mid]) / 2)
    : sorted[mid];
  return {
    medianCents,
    lowCents: Math.round(medianCents * 0.94),
    highCents: Math.round(medianCents * 1.06),
  };
}

// ---------- Listing optimization (mid/long channels) ----------

export type MidLongChannel = 'Zillow' | 'Apartments.com' | 'Zumper' | 'Furnished Finder';

export type ListingStatus = 'Listed' | 'Not listed' | 'Paused';

export interface ChannelListingRow {
  channel: MidLongChannel;
  status: ListingStatus;
  completenessPct: number;
  suggestions: string[];
}

const CHANNEL_ROWS_BY_ADDRESS: Record<number, ChannelListingRow[]> = {
  990001: [
    { channel: 'Zillow',            status: 'Listed',     completenessPct: 92, suggestions: ['Answer 2 unanswered questions'] },
    { channel: 'Apartments.com',    status: 'Listed',     completenessPct: 78, suggestions: ['Add a floor plan photo', 'Mention in-unit laundry in the title'] },
    { channel: 'Zumper',            status: 'Paused',     completenessPct: 64, suggestions: ['Resume listing to restore search visibility', 'Add 2 more exterior photos'] },
    { channel: 'Furnished Finder',  status: 'Listed',     completenessPct: 88, suggestions: ['Add a kitchen close-up photo'] },
  ],
  990002: [
    { channel: 'Zillow',            status: 'Listed',     completenessPct: 85, suggestions: ['Add a floor plan photo'] },
    { channel: 'Apartments.com',    status: 'Not listed', completenessPct: 0,  suggestions: ['Create a listing to reach renters browsing this channel', 'Import photos from Zillow to save time'] },
    { channel: 'Zumper',            status: 'Listed',     completenessPct: 71, suggestions: ['Mention in-unit laundry in the title', 'Answer 1 unanswered question'] },
    { channel: 'Furnished Finder',  status: 'Listed',     completenessPct: 95, suggestions: [] },
  ],
  990003: [
    { channel: 'Zillow',            status: 'Listed',     completenessPct: 81, suggestions: ['Add a floor plan photo', 'Answer 3 unanswered questions'] },
    { channel: 'Apartments.com',    status: 'Listed',     completenessPct: 90, suggestions: ['Mention in-unit laundry in the title'] },
    { channel: 'Zumper',            status: 'Listed',     completenessPct: 58, suggestions: ['Add a floor plan photo', 'Add 2 more exterior photos', 'Answer 1 unanswered question'] },
    { channel: 'Furnished Finder',  status: 'Paused',     completenessPct: 70, suggestions: ['Resume listing to restore search visibility'] },
  ],
};

export const MID_LONG_CHANNELS: MidLongChannel[] = ['Zillow', 'Apartments.com', 'Zumper', 'Furnished Finder'];

export function getChannelRows(addressId: number): ChannelListingRow[] {
  return CHANNEL_ROWS_BY_ADDRESS[addressId] ?? CHANNEL_ROWS_BY_ADDRESS[990001];
}

// Real accounts have no mid/long-term listing pipeline yet — the channels
// still render so the section reads as "nothing connected", but every bar is
// empty rather than showing demo completeness.
export function emptyChannelRows(): ChannelListingRow[] {
  return MID_LONG_CHANNELS.map(channel => ({
    channel,
    status: 'Not listed' as ListingStatus,
    completenessPct: 0,
    suggestions: [],
  }));
}

export function formatCents(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString()}`;
}
